import { ChangeDetectorRef, ViewChild } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { StorageService } from 'src/app/shared/services/storage.service';
import { BuildingService } from 'src/app/shared/services/rest/building.service';
import { Router, ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'src/app/shared/services/message.service';
import { SearchForm } from './models/search-form';
import { AllocationTableModel } from './components/room-allocation-table/room-allocation-desktop.component';
import { SessionSettings } from 'src/app/shared/models/client-settings.model';
import { ModalDialogComponent } from 'src/app/shared/components/modal-dialog/modal-dialog.component';
import { IBaseSearchForm } from './models/i-base-search-form';
import { DateHelper } from 'src/app/shared/utils/date-helper';
import { ICreateReservationParams } from './components/create-reservation-dialog/create-reservation-dialog-base.component';
import { ResourceType } from 'src/app/shared/models/resource-type';
import { AccordionDirective } from 'src/app/shared/components/accordion/accordion.directive';
import { IRoomScheduleInfo } from './components/room-allocation-table/room-allocation-table.component';

export enum AvailabilityType {
  // from - to
  absolut = 'abs',
  // eg. 1:00
  span = 'span'
}

export abstract class BaseReservationComponent<TSearchForm extends IBaseSearchForm> {

  protected restoringSessionData = false;

  protected eventsAttached = false;

  // services
  protected storage: StorageService;
  protected fb: FormBuilder;
  protected building: BuildingService;
  protected router: Router;
  protected activatedRoute: ActivatedRoute;
  protected translate: TranslateService;
  protected messages: MessageService;

  isCollapsedInfoText: boolean;

  /**
   * SearchForm instance of this component
   *
   * @protected
   * @type {SearchForm<TSearchForm>}
   * @memberof BaseReservationComponent
   */
  protected searchForm: SearchForm<TSearchForm>;

  public allocationTableModel: AllocationTableModel;

  /**
   * Selected date (is set on click to search)
   *
   * @type {Date}
   * @memberof BaseReservationComponent
   */
  currentDate: Date;

  /**
  * params provided to the reservation-creation dialog
  *
  * @type {ICreateReservationParams}
  * @memberof RoomReservationComponent
  */
  createReservationParams: ICreateReservationParams;

  /**
   * Currently active room id (eg. for room schedule)
   *
   * @type {number}
   * @memberof RoomReservationComponent
   */
  activeItemId: number;

  activeRessourceInfo: IRoomScheduleInfo;

  /**
   * Accordion component
   *
   * @private
   * @type {ClxAccordionComponent}
   * @memberof BaseReservationComponent
   */
  @ViewChild(AccordionDirective) protected accordion: AccordionDirective;

  /**
   * Create reservation dialog component
   *
   * @private
   * @type {ModalDialogComponent}
   * @memberof BaseReservationComponent
   */
  @ViewChild('createReservationDialog') private createReservationDialog: ModalDialogComponent;

  @ViewChild('roomScheduleDialog') protected modalRoomSchedule: ModalDialogComponent;

  /**
   * Indicates wheter the component is loading data or not
   *
   * @type {boolean}
   * @memberof BaseReservationComponent
   */
  isLoading: boolean;

  constructor(
    storage: StorageService,
    fb: FormBuilder,
    building: BuildingService,
    router: Router,
    activatedRoute: ActivatedRoute,
    translate: TranslateService,
    messages: MessageService,
    protected readonly cdr: ChangeDetectorRef
  ) {
    this.storage = storage;
    this.fb = fb;
    this.building = building;
    this.router = router;
    this.activatedRoute = activatedRoute;
    this.translate = translate;
    this.messages = messages;

    this.currentDate = DateHelper.getCurrentDate();
    this.createForm();
  }

  /**
   * Hook to start search
   *
   * @protected
   * @abstract
   * @memberof BaseReservationComponent
   */
  protected abstract executeSearch(): void;

  /**
   * Hook to initialize the search form
   *
   * @protected
   * @abstract
   * @memberof BaseReservationComponent
   */
  protected abstract createForm(): void;

  /**
   * Handle click on search-button
   *
   * @memberof BaseReservationComponent
   */
  public onSearchClick() {
    this.executeSearch();
  }

  /**
   * Handle click on date navigation button
   *
   * @param {boolean} backwards
   * @memberof BaseReservationComponent
   */
  onNavigateDate(backwards: boolean) {
    const newDate = DateHelper.parseIsoDateString(this.searchForm.getValue('date'));
    newDate.setDate(newDate.getDate() + (backwards ? -1 : 1));
    this.searchForm.patchValue('date', newDate);
    this.executeSearch();
  }
  /**
   * spans a date time by the amounts of minutes given,
   * without exceeding a deadline if the is one given
   * @param startDate
   * @param spanInMin
   * @param deadline
   */
  spanTimeByMinutes(startDate: Date, spanInMin: number, deadline?: Date) {
    const d = new Date(startDate);
    d.setHours(startDate.getHours());
    d.setMinutes(startDate.getMinutes() + spanInMin);
    return deadline && d > deadline ? deadline : d;
  }

  /**
   * Open dialog to create a new reservation
   *
   * @param {ICreateReservationParams} params
   * @memberof RoomReservationComponent
   */
  async onCreateReservation(params: ICreateReservationParams) {
    // set the end time 15 minutes from now, instead of using end time of the slot
    // TODO: use a configuration instead of hard coded 15 minutes
    params.to = this.spanTimeByMinutes(params.from, 15, params.to);

    this.createReservationParams = params;
    // add info from search-form
    if (!!this.searchForm.getValue('showAvailable')) {
      this.adjustReservationParams(this.createReservationParams);
    }
    this.createReservationDialog.openModal();
  }

  /**
   * Handle save of reservation
   *
   * @param {string} location
   * @memberof RoomReservationComponent
   */
  onReservationSaved(location: string) {
    // close create
    this.createReservationDialog.closeModal(null);

    this.onAfterReservationSaved(location);
  }

  /**
   * Type of resources to display
   *
   * @readonly
   * @abstract
   * @type {ResourceType}
   * @memberof BaseReservationComponent
   */
  abstract get resourceType(): ResourceType;

  /**
   * After reservation saved hook
   *
   * @protected
   * @abstract
   * @param {string} location
   * @memberof BaseReservationComponent
   */
  protected abstract onAfterReservationSaved(location: string);

  /**
   * Calculate desired start and end date according to selection in form
   *
   * @private
   * @param {Date} startDate
   * @memberof RoomReservationComponent
   */
  protected adjustReservationParams(params: ICreateReservationParams) {
    const type = this.searchForm.getValue('showAvailableType');
    if (type === AvailabilityType.absolut) {
      const from = Number(this.searchForm.getValue('showAvailableTimeFrom'));
      const to = Number(this.searchForm.getValue('showAvailableTimeTo'));
      DateHelper.setTimeFromMinutesOfDay(from, params.from);
      DateHelper.setTimeFromMinutesOfDay(to, params.to);
    } else {
      const minutes = Number(this.searchForm.getValue('showAvailableHours'));
      params.to = new Date(params.from);
      params.to.setMinutes(params.from.getMinutes() + minutes);
    }
  }

  /**
   * Attach events hook
   *
   * @protected
   * @memberof BaseReservationComponent
   */
  protected onAttachEvents() { }

  /**
   * Attach events to form etc.
   *
   * @protected
   * @memberof BaseReservationComponent
   */
  protected attachEvents() {
    if (!this.eventsAttached) {
      this.eventsAttached = true;
      this.onAttachEvents();
    }
  }

  /**
   * Returns the settings property for given form
   *
   * @protected
   * @abstract
   * @param {SessionSettings} x
   * @returns {TSearchForm}
   * @memberof BaseReservationComponent
   */
  protected abstract getSessionSettingsKey(): keyof SessionSettings;

  /**
   * After session data restore hook
   *
   * @protected
   * @memberof BaseReservationComponent
   */
  protected OnAfterRestoreSessionData() {
  }

  /**
   * Load session search-form settings from sessionStorage
   *
   * @protected
   * @memberof BaseReservationComponent
   */
  protected restoreSessionData() {
    const sessionSettings = this.storage.sessionSettings.getValue();
    const value = sessionSettings[this.getSessionSettingsKey()];
    if (value) {
      try {
        this.restoringSessionData = true;
        this.searchForm.formGroup.patchValue(value);
        this.OnAfterRestoreSessionData();
      }
      finally {
        this.restoringSessionData = false;
      }
    }
  }

  /**
   * Save current search-form to sessionStorage
   *
   * @protected
   * @memberof BaseReservationComponent
   */
  protected updateSessionData() {
    this.storage.updateSessionSettings((currentSettings) => {
      if (!this.restoringSessionData) { // do not save on running restore
        currentSettings[this.getSessionSettingsKey()] = this.searchForm.formGroup.value as any;
      }
    });
  }
}
